import { useMutation } from '@apollo/client'
import { useCallback } from 'react'
import {
  EXPIRE_SESSION,
  type IExpireSession,
  type IExpireSessionVariables,
} from '@commerce/core'

export interface UseExpireSession {
  /**
   * Ends the session. Resolves either way — see the note on failure below.
   */
  expireSession: (slug: string, sessionId: string) => Promise<void>
  loading: boolean
}

/**
 * Logging out, for real.
 *
 * Without this, "Cerrar sesión" only navigated: the session stayed ACTIVE, and
 * `createSession` reuses an active session for a store code, so the next login
 * returned the same session — same cart, same selections, and the same empty
 * promotions if its stored customer had been emptied.
 *
 * A failure resolves rather than throws. There is nothing the shopper can do
 * about it and nothing they would want to do: the intent is "get me out", so
 * the screen should leave regardless. The cost of a silent failure is a
 * session that expires on its own `sessionTtl` instead of now; the cost of
 * throwing is stranding someone on a screen they asked to leave.
 */
export const useExpireSession = (): UseExpireSession => {
  const [mutate, { loading }] = useMutation<IExpireSession, IExpireSessionVariables>(
    EXPIRE_SESSION,
  )

  const expireSession = useCallback(
    async (slug: string, sessionId: string): Promise<void> => {
      if (!slug || !sessionId) return
      try {
        await mutate({ variables: { storefrontName: slug, id: sessionId } })
      } catch {
        // Deliberately swallowed — see the note above.
      }
    },
    [mutate],
  )

  return { expireSession, loading }
}
